import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { telemetryService } from '@services/api/telemetryService';
import { customerService } from '@services/api/customerService';
import type { AppEvent } from '@/types/customer';
import './SessionDetail.css';

interface EventDetailsModalProps {
  event: AppEvent;
  onClose: () => void;
}

const EventDetailsModal = ({ event, onClose }: EventDetailsModalProps) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Event Details</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="event-detail-grid">
            <div className="event-detail-item">
              <span className="event-detail-label">Event ID:</span>
              <span className="event-detail-value">{event.id}</span>
            </div>
            <div className="event-detail-item">
              <span className="event-detail-label">Event Type:</span>
              <span className="event-detail-value">{event.event_type}</span>
            </div>
            <div className="event-detail-item">
              <span className="event-detail-label">Severity:</span>
              <span className={`severity-badge severity-${event.severity}`}>
                {event.severity}
              </span>
            </div>
            <div className="event-detail-item">
              <span className="event-detail-label">Screen:</span>
              <span className="event-detail-value">{event.screen || 'N/A'}</span>
            </div>
            <div className="event-detail-item">
              <span className="event-detail-label">Action:</span>
              <span className="event-detail-value">{event.action || 'N/A'}</span>
            </div>
            <div className="event-detail-item">
              <span className="event-detail-label">Created At:</span>
              <span className="event-detail-value">
                {new Date(event.created_at).toLocaleString()}
              </span>
            </div>
          </div>

          {event.error_message && (
            <div className="event-detail-section">
              <h3>Error Message</h3>
              <pre className="event-detail-code">{event.error_message}</pre>
            </div>
          )}

          {event.error_stack && (
            <div className="event-detail-section">
              <h3>Error Stack</h3>
              <pre className="event-detail-code">{event.error_stack}</pre>
            </div>
          )}

          {event.properties && Object.keys(event.properties).length > 0 && (
            <div className="event-detail-section">
              <h3>Properties</h3>
              <pre className="event-detail-code">
                {JSON.stringify(event.properties, null, 2)}
              </pre>
            </div>
          )}

          {event.device && Object.keys(event.device).length > 0 && (
            <div className="event-detail-section">
              <h3>Device Info</h3>
              <pre className="event-detail-code">
                {JSON.stringify(event.device, null, 2)}
              </pre>
            </div>
          )}

          {event.network && Object.keys(event.network).length > 0 && (
            <div className="event-detail-section">
              <h3>Network Info</h3>
              <pre className="event-detail-code">
                {JSON.stringify(event.network, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SessionDetail = () => {
  const { customerId, sessionId } = useParams();
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [session, setSession] = useState<any>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<AppEvent | null>(null);

  // Filters
  const [filters, setFilters] = useState({
    event_type: '',
    severity: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    loadSessionData();
  }, [sessionId, filters]);

  const loadSessionData = async () => {
    if (!sessionId || !customerId) return;

    setLoading(true);

    // Load session, customer, and events
    const [sessionRes, customerRes, eventsRes] = await Promise.all([
      customerService.getCustomerSessions(customerId, 100),
      customerService.getCustomerById(customerId, 'system'),
      telemetryService.getSessionEvents(sessionId, filters as any),
    ]);

    if (sessionRes.success && sessionRes.data) {
      const foundSession = sessionRes.data.find((s: any) => s.id === sessionId);
      if (foundSession) setSession(foundSession);
    }

    if (customerRes.success && customerRes.data) {
      setCustomer(customerRes.data as any);
    }

    if (eventsRes.success && eventsRes.data) {
      setEvents(eventsRes.data as any);
    }

    setLoading(false);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ 
      ...prev, 
      [key]: key === 'severity' ? (value as any) : value 
    }));
  };

  const clearFilters = () => {
    setFilters({
      event_type: '',
      severity: undefined as any,
      date_from: '',
      date_to: '',
    });
  };

  const uniqueEventTypes = Array.from(new Set(events.map((e) => e.event_type))).sort();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!session) {
    return <div>Session not found</div>;
  }

  return (
    <div className="session-detail">
      <div className="session-detail-header">
        <Link 
          to={`/customers/${customerId}`} 
          className="session-back-button"
        >
          ← Back to Customer
        </Link>

        <div className="session-title">
          <h1 className="page-title">Session Details</h1>
          <span className="session-id-badge">
            Session ID: {session.id.slice(0, 13)}...
          </span>
        </div>
      </div>

      {/* Session Info Card */}
      <div className="info-card">
        <h3 className="info-card-title">📱 Session Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Customer:</span>
            <span className="info-value">
              {customer ? `${customer.first_name} ${customer.last_name}` : 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Device:</span>
            <span className="info-value">
              {session.device?.device_name || session.device?.device_type || 'Unknown'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">OS:</span>
            <span className="info-value">
              {session.device?.os_name || 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Status:</span>
            <span className={`status-badge status-${
              session.status === 'active' ? 'green' : 
              session.status === 'ended' ? 'gray' : 'orange'
            }`}>
              {session.status}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Start Time:</span>
            <span className="info-value">
              {new Date(session.start_time).toLocaleString()}
            </span>
          </div>
          {session.end_time && (
            <div className="info-item">
              <span className="info-label">End Time:</span>
              <span className="info-value">
                {new Date(session.end_time).toLocaleString()}
              </span>
            </div>
          )}
          <div className="info-item">
            <span className="info-label">Current Page:</span>
            <span className="info-value">{session.current_page || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Total Events:</span>
            <span className="info-value">{events.length}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="info-card">
        <h3 className="info-card-title">🔍 Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Event Type:</label>
            <select
              className="filter-select"
              value={filters.event_type}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
            >
              <option value="">All Types</option>
              {uniqueEventTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Severity:</label>
            <select
              className="filter-select"
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
            >
              <option value="">All Severities</option>
              <option value="info">Info</option>
              <option value="warn">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Date From:</label>
            <input
              type="datetime-local"
              className="filter-input"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Date To:</label>
            <input
              type="datetime-local"
              className="filter-input"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
            />
          </div>

          <button className="btn btn-secondary btn-small" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Events Table */}
      <div className="info-card">
        <h3 className="info-card-title">📊 Session Events ({events.length})</h3>
        
        {events.length === 0 ? (
          <p className="text-secondary">No events found</p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Created At</th>
                  <th>Event Type</th>
                  <th>Severity</th>
                  <th>Screen</th>
                  <th>Action</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id}>
                    <td className="text-small">
                      {new Date(event.created_at).toLocaleString()}
                    </td>
                    <td className="text-small">{event.event_type}</td>
                    <td>
                      <span className={`severity-badge severity-${event.severity}`}>
                        {event.severity}
                      </span>
                    </td>
                    <td className="text-small">{event.screen || 'N/A'}</td>
                    <td className="text-small">{event.action || 'N/A'}</td>
                    <td>
                      <button
                        className="btn btn-ghost btn-small"
                        onClick={() => setSelectedEvent(event)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
};

